import React, { useState, useEffect } from 'react';
import Button      from '../components/ui/Button';
import Input       from '../components/ui/Input';
import Icon        from '../components/ui/Icon';
import { useAuth } from '../context/AuthContext';
import useRegister from '../hooks/useRegister';
import { useEnabledLocations } from '../hooks/useLocations';

const getFlag = (cc) =>
  [...cc.toUpperCase()].map((c) => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('');

/* ─── Validation ──────────────────────────────────────────────── */
const validateStep1 = (v) => {
  const e = {};
  if (!v.first_name || v.first_name.trim().length < 2)
    e.first_name = 'First name is required (min 2 chars)';
  if (!v.last_name || v.last_name.trim().length < 2)
    e.last_name = 'Last name is required (min 2 chars)';
  if (!v.dob)
    e.dob = 'Date of birth is required';
  else {
    const age = Math.floor(
      (Date.now() - new Date(v.dob).getTime()) / (365.25 * 24 * 3600 * 1000),
    );
    if (age < 15) e.dob = 'You must be at least 15 years old';
    if (age > 100) e.dob = 'Enter a valid date of birth';
  }
  return e;
};

const validateStep2 = (v) => {
  const e = {};
  if (!v.phone_number || !/^\d{7,15}$/.test(v.phone_number.replace(/\s/g, '')))
    e.phone_number = 'Enter a valid phone number';
  if (!v.email || !/\S+@\S+\.\S+/.test(v.email))
    e.email = 'Enter a valid email address';
  if (!v.password)
    e.password = 'Password is required';
  else if (v.password.length < 8)
    e.password = 'Minimum 8 characters';
  else if (!/(?=.*[A-Z])/.test(v.password))
    e.password = 'Must include at least one uppercase letter';
  else if (!/(?=.*\d)/.test(v.password))
    e.password = 'Must include at least one number';
  else if (!/(?=.*[!@#$%^&*])/.test(v.password))
    e.password = 'Must include at least one special character (!@#$%^&*)';
  return e;
};

/* ─── Password strength ───────────────────────────────────────── */
const getPasswordStrength = (pwd) => {
  if (!pwd) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pwd.length >= 8)                   score++;
  if (/[A-Z]/.test(pwd))                 score++;
  if (/\d/.test(pwd))                    score++;
  if (/[!@#$%^&*]/.test(pwd))            score++;
  if (pwd.length >= 12)                  score++;
  if (score <= 1) return { score, label: 'Weak',    color: 'bg-rose-400'   };
  if (score <= 2) return { score, label: 'Fair',    color: 'bg-amber-400'  };
  if (score <= 3) return { score, label: 'Good',    color: 'bg-sky-400'    };
  return           { score, label: 'Strong',  color: 'bg-emerald-400' };
};

/* ─── Phone Input ─────────────────────────────────────────────── */
const PhoneInput = ({ countries = [], countryCode, onCountryChange, phoneValue, onPhoneChange, onPhoneBlur, error }) => {
  const [open, setOpen] = useState(false);
  const selected = countries.find((c) => c.dial_code === countryCode) || countries[0];

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-dm font-medium text-surface-100 uppercase tracking-wider">
        Phone Number
      </label>
      <div className="flex gap-2">
        {/* Country code picker */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((p) => !p)}
            className="h-[52px] px-3 rounded-2xl bg-surface-600 border border-surface-400 flex items-center gap-1.5 font-dm text-sm text-slate-800 press-scale whitespace-nowrap"
          >
            {selected ? (
              <>
                <span>{getFlag(selected.country_code)}</span>
                <span className="font-medium">{selected.dial_code}</span>
              </>
            ) : (
              <span className="text-surface-300">—</span>
            )}
            <Icon name="arrow" size={12} className={`text-surface-300 transition-transform ${open ? 'rotate-90' : ''}`} />
          </button>
          {open && countries.length > 0 && (
            <div className="absolute top-[56px] left-0 z-50 bg-white border border-surface-400 rounded-2xl shadow-lg overflow-hidden min-w-[160px] max-h-60 overflow-y-auto">
              {countries.map((c) => (
                <button
                  key={c.country_code}
                  type="button"
                  onClick={() => { onCountryChange(c.dial_code); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 font-dm text-sm text-left hover:bg-brand-50 transition-colors ${c.dial_code === countryCode ? 'bg-brand-50 text-brand-600 font-semibold' : 'text-slate-700'}`}
                >
                  <span>{getFlag(c.country_code)}</span>
                  <span>{c.dial_code}</span>
                  <span className="text-surface-300 text-xs ml-auto">{c.country_code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone number */}
        <div className="flex-1 relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-300 pointer-events-none">
            <Icon name="phone" size={17} />
          </span>
          <input
            name="phone_number"
            type="tel"
            inputMode="numeric"
            placeholder="1234567890"
            value={phoneValue}
            onChange={onPhoneChange}
            onBlur={onPhoneBlur}
            className={[
              'w-full h-[52px] rounded-2xl bg-surface-600 border text-sm font-dm text-slate-800 pl-11 pr-4',
              'transition-all duration-200 focus:outline-none',
              error
                ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15'
                : 'border-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15',
            ].join(' ')}
          />
        </div>
      </div>
      {error && (
        <p className="text-xs text-rose-500 font-dm flex items-center gap-1">
          <span>⚠</span> {error}
        </p>
      )}
    </div>
  );
};

/* ─── DOB Input ───────────────────────────────────────────────── */
const DobInput = ({ value, onChange, onBlur, error }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-dm font-medium text-surface-100 uppercase tracking-wider">
      Date of Birth
    </label>
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-300 pointer-events-none">
        <Icon name="clock" size={17} />
      </span>
      <input
        name="dob"
        type="date"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        max={new Date(Date.now() - 15 * 365.25 * 24 * 3600 * 1000).toISOString().split('T')[0]}
        className={[
          'w-full h-[52px] rounded-2xl bg-surface-600 border text-sm font-dm text-slate-800 pl-11 pr-4',
          'transition-all duration-200 focus:outline-none',
          error
            ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15'
            : 'border-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15',
        ].join(' ')}
      />
    </div>
    {error && (
      <p className="text-xs text-rose-500 font-dm flex items-center gap-1">
        <span>⚠</span> {error}
      </p>
    )}
  </div>
);

/* ─── Main Component ──────────────────────────────────────────── */
const INITIAL = {
  first_name:   '',
  middle_name:  '',
  last_name:    '',
  dob:          '',
  country_code: '',
  phone_number: '',
  email:        '',
  password:     '',
};

const RegisterPage = () => {
  const { goTo } = useAuth();
  const registerMutation = useRegister();
  const { data: countries = [] } = useEnabledLocations();

  useEffect(() => {
    if (countries.length > 0) {
      setValues((v) => v.country_code ? v : { ...v, country_code: countries[0].dial_code });
    }
  }, [countries]);

  const [step,    setStep]    = useState(1);
  const [values,  setValues]  = useState(INITIAL);
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});

  /* helpers */
  const change = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (touched[name]) {
      const validator = step === 1 ? validateStep1 : validateStep2;
      const errs = validator({ ...values, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: errs[name] }));
    }
  };

  const blur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    const validator = step === 1 ? validateStep1 : validateStep2;
    const errs = validator(values);
    setErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const countryChange = (code) => {
    setValues((v) => ({ ...v, country_code: code }));
  };

  /* step 1 → 2 */
  const nextStep = () => {
    const errs = validateStep1(values);
    const step1Fields = ['first_name', 'last_name', 'dob'];
    setErrors(errs);
    setTouched(step1Fields.reduce((a, k) => ({ ...a, [k]: true }), {}));
    if (Object.keys(errs).length === 0) setStep(2);
  };

  /* final submit */
  const onSubmit = () => {
    const errs = validateStep2(values);
    const step2Fields = ['phone_number', 'email', 'password'];
    setErrors(errs);
    setTouched((t) => ({ ...t, ...step2Fields.reduce((a, k) => ({ ...a, [k]: true }), {}) }));
    if (Object.keys(errs).length > 0) return;

    registerMutation.mutate({
      first_name:   values.first_name,
      middle_name:  values.middle_name,
      last_name:    values.last_name,
      dob:          values.dob,
      country_code: values.country_code,
      phone_number: values.phone_number,
      email:        values.email,
      password:     values.password,
    });
  };

  const strength = getPasswordStrength(values.password);

  return (
    <div className="page-scroll flex flex-col min-h-full bg-surface-900 relative overflow-hidden">
      <div className="noise-overlay" />
      <div
        className="absolute -top-16 -left-16 w-56 h-56 rounded-full opacity-[0.08] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }}
      />
      <div
        className="absolute bottom-10 -right-20 w-48 h-48 rounded-full opacity-[0.05] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #818cf8, transparent 70%)' }}
      />

      {/* Header */}
      <div className="px-6 pt-14 pb-4 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
        <button
          onClick={() => (step === 2 ? setStep(1) : goTo('login'))}
          className="w-9 h-9 rounded-xl bg-surface-700 flex items-center justify-center text-surface-200 hover:text-slate-800 mb-5 press-scale border border-surface-400"
        >
          <Icon name="arrowLeft" size={17} />
        </button>

        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 brand-glow"
          style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
        >
          <span className="text-xl">👤</span>
        </div>

        <p className="font-dm text-surface-200 text-xs uppercase tracking-widest mb-1">Get started</p>
        <h1 className="font-syne font-extrabold text-slate-800 text-[1.8rem] leading-tight">
          Create your<br /><span className="text-gradient">account</span>
        </h1>
      </div>

      {/* Step indicator */}
      <div className="px-6 pb-4 animate-fadeUp s1" style={{ animationFillMode: 'forwards', opacity: 0 }}>
        <div className="flex items-center gap-2">
          {[
            { num: 1, label: 'Personal' },
            { num: 2, label: 'Contact'  },
          ].map((s, i, arr) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center font-dm font-bold text-xs transition-all duration-300 ${step >= s.num ? 'text-white' : 'bg-surface-700 border border-surface-400 text-surface-200'}`}
                  style={step >= s.num ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)' } : {}}
                >
                  {step > s.num ? <Icon name="check" size={12} /> : s.num}
                </div>
                <span className={`font-dm text-xs ${step >= s.num ? 'text-slate-700 font-medium' : 'text-surface-300'}`}>
                  {s.label}
                </span>
              </div>
              {i < arr.length - 1 && (
                <div className={`flex-1 h-px transition-colors duration-300 ${step > s.num ? 'bg-brand-500' : 'bg-surface-500'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── STEP 1: Personal Info ─────────────────────────────── */}
      {step === 1 && (
        <div className="flex-1 px-5 pb-10 animate-scaleIn" style={{ animationFillMode: 'forwards' }}>
          <div className="glass rounded-3xl p-5 card-shadow animate-fadeUp s2" style={{ animationFillMode: 'forwards' }}>
            <p className="font-syne font-bold text-slate-800 text-sm mb-4">Personal Details</p>
            <div className="flex flex-col gap-4">
              <Input
                label="First Name *" name="first_name" placeholder="Alex"
                value={values.first_name} onChange={change} onBlur={blur}
                error={touched.first_name && errors.first_name}
                icon="user"
              />
              <Input
                label="Last Name *" name="last_name" placeholder="Smith"
                value={values.last_name} onChange={change} onBlur={blur}
                error={touched.last_name && errors.last_name}
                icon="user"
              />

              <Input
                label="Middle Name (optional)" name="middle_name" placeholder="Jordan"
                value={values.middle_name} onChange={change} onBlur={blur}
                icon="user"
              />

              <DobInput
                value={values.dob} onChange={change} onBlur={blur}
                error={touched.dob && errors.dob}
              />
            </div>
          </div>

          <div className="mt-4 animate-fadeUp s3" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            <Button size="lg" onClick={nextStep}>
              Continue <Icon name="arrow" size={16} />
            </Button>
          </div>

          <p className="text-center font-dm text-sm text-surface-200 mt-5 animate-fadeUp s4" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            Already have an account?{' '}
            <button onClick={() => goTo('login')} className="text-brand-600 font-semibold hover:text-brand-500 transition-colors">
              Sign in
            </button>
          </p>
        </div>
      )}

      {/* ── STEP 2: Contact & Security ────────────────────────── */}
      {step === 2 && (
        <div className="flex-1 px-5 pb-10 animate-scaleIn" style={{ animationFillMode: 'forwards' }}>
          <div className="glass rounded-3xl p-5 card-shadow animate-fadeUp s2" style={{ animationFillMode: 'forwards' }}>
            <p className="font-syne font-bold text-slate-800 text-sm mb-4">Contact & Security</p>
            <div className="flex flex-col gap-4">
              <PhoneInput
                countries={countries}
                countryCode={values.country_code}
                onCountryChange={countryChange}
                phoneValue={values.phone_number}
                onPhoneChange={change}
                onPhoneBlur={blur}
                error={touched.phone_number && errors.phone_number}
              />

              <Input
                label="Email Address *" name="email" type="email" placeholder="you@email.com"
                value={values.email} onChange={change} onBlur={blur}
                error={touched.email && errors.email}
                icon="mail"
              />

              <div>
                <Input
                  label="Password *" name="password" type="password"
                  placeholder="Min 8 chars, uppercase, number & symbol"
                  value={values.password} onChange={change} onBlur={blur}
                  error={touched.password && errors.password}
                  icon="lock"
                />
                {/* Strength bar */}
                {values.password && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${strength.score >= i ? strength.color : 'bg-surface-500'}`}
                        />
                      ))}
                    </div>
                    <p className={`font-dm text-xs ${strength.score <= 1 ? 'text-rose-500' : strength.score <= 2 ? 'text-amber-500' : strength.score <= 3 ? 'text-sky-500' : 'text-emerald-600'}`}>
                      {strength.label} password
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Terms */}
          <p className="font-dm text-xs text-surface-200 text-center mt-4 px-2 animate-fadeUp s3" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            By registering, you agree to our{' '}
            <span className="text-brand-600 font-medium">Terms of Service</span> &{' '}
            <span className="text-brand-600 font-medium">Privacy Policy</span>
          </p>

          <div className="mt-3 animate-fadeUp s3" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            <Button size="lg" loading={registerMutation.isPending} onClick={onSubmit}>
              <Icon name="check" size={16} />
              Create Account
            </Button>
          </div>

          <p className="text-center font-dm text-sm text-surface-200 mt-5 animate-fadeUp s4" style={{ animationFillMode: 'forwards', opacity: 0 }}>
            Already have an account?{' '}
            <button onClick={() => goTo('login')} className="text-brand-600 font-semibold hover:text-brand-500 transition-colors">
              Sign in
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
